import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateTimeAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, Shield } from 'lucide-react';

export default function ClientAudit() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudit = async () => {
      if (!token || !clientId) return;
      try {
        const response = await api.getAudit(token, clientId);
        setAuditEvents(response.data);
      } catch (error) {
        console.error('Failed to fetch audit:', error);
      }
      setLoading(false);
    };
    fetchAudit();
  }, [token, clientId]);

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-[#6B8E6B]/10 text-[#6B8E6B]';
      case 'update': return 'bg-[#4A3B32]/10 text-[#4A3B32]';
      case 'verify': return 'bg-[#6B8E6B]/10 text-[#6B8E6B]';
      case 'flag': return 'bg-[#D99A6C]/10 text-[#D99A6C]';
      case 'status_change': return 'bg-[#7D8C9E]/10 text-[#7D8C9E]';
      case 'view': return 'bg-[#F0EBE6] text-[#5D524D]';
      default: return 'bg-[#F0EBE6] text-[#5D524D]';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="audit-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Audit Log</h1>
          <p className="text-[#5D524D]">Complete audit trail for {client?.name}</p>
        </div>
      </div>

      {/* Restricted Note */}
      {client?.restricted && (
        <div className="bg-[#7D8C9E]/10 border border-[#7D8C9E]/20 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#7D8C9E]" />
          <p className="text-sm text-[#5D524D]">
            All access to this restricted client is logged and audited.
          </p>
        </div>
      )}

      {/* Audit Table */}
      {auditEvents.length > 0 ? (
        <div className="caffeine-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Object</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Outcome</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {auditEvents.map(event => (
                <tr key={event.id} data-testid={`audit-${event.id}`}>
                  <td className="font-mono text-sm">{formatDateTimeAU(event.timestamp)}</td>
                  <td className="font-medium text-[#2C2420]">{event.object}</td>
                  <td>{event.actor}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded text-xs ${getActionColor(event.action)}`}>
                      {event.action.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{event.outcome}</td>
                  <td className="font-mono text-sm text-[#8C8078]">{event.ipAddress}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Clock className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No audit events</p>
          <p className="text-sm">No audit events recorded for this client</p>
        </div>
      )}
    </div>
  );
}
